import React, { useState } from 'react';
import { useParent } from '../context/ParentContext';
import './ParentSettings.css';

function ParentSettings() {
  const { parentProfile, alertPreferences, updateAlertPreferences } = useParent();
  const [activeSection, setActiveSection] = useState('profile');

  const sections = [
    { key: 'profile', label: 'Profile' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'privacy', label: 'Privacy' },
    { key: 'display', label: 'Display' },
  ];

  return (
    <div className="parent-settings">
      <h1 className="parent-page-title">Settings</h1>

      <div className="parent-settings__layout">
        <nav className="parent-settings__nav" aria-label="Settings sections">
          {sections.map(s => (
            <button
              key={s.key}
              className={`parent-settings__nav-item ${activeSection === s.key ? 'parent-settings__nav-item--active' : ''}`}
              onClick={() => setActiveSection(s.key)}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div className="parent-settings__content">
          {activeSection === 'profile' && (
            <section className="parent-settings__section">
              <h2>Profile</h2>
              <div className="parent-settings__field">
                <label>Name</label>
                <span>{parentProfile.firstName} {parentProfile.lastName}</span>
              </div>
              <div className="parent-settings__field">
                <label>Email</label>
                <span>{parentProfile.email}</span>
              </div>
              <div className="parent-settings__field">
                <label>Relationship</label>
                <span>{parentProfile.relationship}</span>
              </div>
              <div className="parent-settings__field">
                <label>Phone</label>
                <span>{parentProfile.phone}</span>
              </div>
            </section>
          )}

          {activeSection === 'notifications' && (
            <section className="parent-settings__section">
              <h2>Notification Preferences</h2>
              <p className="parent-settings__description">Choose how you want to be notified about your student's activity.</p>
              <div className="parent-settings__toggles">
                <NotificationToggle label="Missing assignments" prefKey="missingAssignments" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="Due within 24 hours" prefKey="dueWithin24Hours" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="Due within 3 days" prefKey="dueWithin3Days" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="Low weekly activity" prefKey="lowWeeklyActivity" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="Declining completion" prefKey="decliningCompletion" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="No recent login" prefKey="noRecentLogin" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="Course risk alerts" prefKey="courseRisk" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="Weekly summary" prefKey="weeklySummary" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
                <NotificationToggle label="Positive milestones" prefKey="positiveMilestones" prefs={alertPreferences} onUpdate={updateAlertPreferences} />
              </div>
            </section>
          )}

          {activeSection === 'privacy' && (
            <section className="parent-settings__section">
              <h2>Privacy</h2>
              <p className="parent-settings__description">
                The Parent Portal provides visibility into academic progress while protecting student privacy.
              </p>
              <div className="parent-settings__privacy-block">
                <h3>What you can see:</h3>
                <ul>
                  <li>Assignment status and due dates</li>
                  <li>Course health and completion rates</li>
                  <li>Study activity summaries</li>
                  <li>Quiz performance summaries</li>
                  <li>Alerts and weekly reports</li>
                </ul>
              </div>
              <div className="parent-settings__privacy-block">
                <h3>What remains private:</h3>
                <ul>
                  <li>Full AI chat transcripts</li>
                  <li>Personal notes content</li>
                  <li>Uploaded private content</li>
                  <li>Passwords and account credentials</li>
                  <li>Non-academic messages</li>
                </ul>
              </div>
            </section>
          )}

          {activeSection === 'display' && (
            <section className="parent-settings__section">
              <h2>Display Preferences</h2>
              <p className="parent-settings__description">Customize how the Parent Portal appears.</p>
              <div className="parent-settings__field">
                <label>Theme</label>
                <span>System default</span>
              </div>
              <div className="parent-settings__field">
                <label>Default view</label>
                <span>Overview</span>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationToggle({ label, prefKey, prefs, onUpdate }) {
  const pref = prefs[prefKey] || {};

  const toggle = (channel) => {
    onUpdate({
      [prefKey]: { ...pref, [channel]: !pref[channel] },
    });
  };

  return (
    <div className="parent-settings__toggle-row">
      <span className="parent-settings__toggle-label">{label}</span>
      <div className="parent-settings__toggle-channels">
        <ToggleButton active={pref.email} onClick={() => toggle('email')} label="Email" />
        <ToggleButton active={pref.inApp} onClick={() => toggle('inApp')} label="In-App" />
        <ToggleButton active={pref.push} onClick={() => toggle('push')} label="Push" />
      </div>
    </div>
  );
}

function ToggleButton({ active, onClick, label }) {
  return (
    <button
      className={`parent-settings__toggle-btn ${active ? 'parent-settings__toggle-btn--active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${label} notifications ${active ? 'enabled' : 'disabled'}`}
    >
      {label}
    </button>
  );
}

export default ParentSettings;
